// PRPricing.js
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const PRPricing = () => {
  const coverageItems = [
    'Initial eligibility assessment and pathway recommendation',
    'Document checklist and preparation guidance',
    'Application form completion and review',
    'Supporting document review and verification',
    'Profile optimization for Express Entry or PNP',
    'Application submission support',
    'Post-submission follow-up and status monitoring',
    'Response to IRCC requests and additional documentation'
  ];

  const importantNotes = [
    'Government processing fees are paid directly to IRCC (Immigration, Refugees and Citizenship Canada)',
    'Notapaths does not collect government fees',
    'Language test fees (IELTS, CELPIP, TEF) are paid directly to test providers',
    'Educational Credential Assessment (ECA) fees are paid directly to designated organizations',
    'Final PR decisions are made by IRCC, not Notapaths. However, 98% of those who follow our guidelines and use our application support succeed. We work closely with immigration requirements to align your application with IRCC standards, hence our exceptional success rate.'
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
              PR Application Support Pricing
            </h2>
          </motion.div>

          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-sm mb-6 border border-gray-200"
          >
            <h3 className="text-xl font-semibold text-[#202356] mb-4">
              Permanent Residency Application Support
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              Our comprehensive PR support service covers all aspects of your permanent residency application, from initial assessment to final submission.
            </p>

            {/* Coverage List */}
            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                Our PR application support includes:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Fee Structure */}
            <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-[#202356] mb-4">
                Service Fee
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#202356]/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-[#202356]">PR Application Support</p>
                    <p className="text-sm text-gray-600">Complete application preparation and submission support</p>
                  </div>
                  <p className="text-2xl font-bold text-[#202356]">$399</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Note:</span> This fee covers our professional services for one PR application pathway. Additional services such as spouse/dependent applications may have separate fees.
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              Final pricing is confirmed after consultation based on your specific pathway and requirements.
            </p>
          </motion.div>

          {/* Important Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-[#202356]">
                Important notes:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>

          {/* Government Fees Reference */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6"
          >
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              Government Fees (Paid Directly to IRCC)
            </h4>
            <div className="space-y-2 text-sm text-gray-700 font-light">
              <p>• <strong>Principal Applicant:</strong> CAD $1,365</p>
              <p>• <strong>Spouse/Partner:</strong> CAD $1,365</p>
              <p>• <strong>Dependent Child:</strong> CAD $230 per child</p>
              <p>• <strong>Right of Permanent Residence Fee:</strong> CAD $515 per adult</p>
              <p className="text-xs text-gray-600 mt-3">
                Note: Government fees are subject to change. Visit the official IRCC website for current fee schedules.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PRPricing;
